import {
    Box,
    FlexScrollArea,
    FlexScrollAreaContainer,
    Grid,
    NavigationRailIcon,
    Stack,
} from "@biggeo/bg-ui/lab";
import {
    CropFreeOutline,
    LayersOutline,
    MapOutline,
    TableRowsOutline,
    TuneOutline,
} from "@biggeo/bg-ui/lab/icons";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import startCase from "lodash/startCase";
import React, { ReactNode, useEffect, useState } from "react";
import { match } from "ts-pattern";
import { MapLayoutHeader } from "../common/components/MapLayoutHeader";
import { MapDrawerLayout, MapDrawerLayoutProps } from "./MapDrawerLayout";

export type MapLayoutTabs =
    | "datasets"
    | "shapeLayers"
    | "boundaries"
    | "maps"
    | "configuration";

const mapLayoutTabs: MapLayoutTabs[] = [
    "datasets",
    "shapeLayers",
    "boundaries",
    "maps",
    "configuration",
];

export type MapLayoutProps = {
    children?: React.ReactNode;
    datasets?: ReactNode;
    shapeLayers?: ReactNode;
    boundaries?: ReactNode;
    maps?: ReactNode;
    configuration?: ReactNode;
    sidebarTab?: MapLayoutTabs;
    datasetsTabLabel?: string;
    onSidebarOpenChange?: (open: boolean, tab: MapLayoutTabs) => void;
    onSidebarItemClick?: (tab: MapLayoutTabs) => void;
} & Pick<
    MapDrawerLayoutProps,
    "bottomSize" | "top" | "bottom" | "leftOpen" | "topOpen" | "bottomOpen"
>;

export const MapLayout = ({
    children,
    datasets,
    shapeLayers,
    boundaries,
    maps,
    configuration,
    leftOpen: _leftOpen = false,
    onSidebarOpenChange,
    bottom,
    bottomOpen,
    top,
    topOpen,
    sidebarTab: _sidebarTab,
    datasetsTabLabel,
    onSidebarItemClick,
    ...sizes
}: MapLayoutProps) => {
    const [open, setOpen] = useState(false);

    const [sidebarTab, setSidebarTab] = useState<MapLayoutTabs>(
        _sidebarTab ?? "datasets"
    );

    useEffect(() => {
        setOpen((prev) => {
            if (_leftOpen !== prev) {
                return _leftOpen;
            }
            return prev;
        });
    }, [_leftOpen]);

    useEffect(() => {
        if (_sidebarTab) {
            setSidebarTab(_sidebarTab);
        }
    }, [_sidebarTab]);

    const getTabIcon = (tab: MapLayoutTabs) =>
        match(tab)
            .with("datasets", () => <TableRowsOutline />)
            .with("shapeLayers", () => <LayersOutline />)
            .with("boundaries", () => <CropFreeOutline />)
            .with("maps", () => <MapOutline />)
            .with("configuration", () => <TuneOutline />)
            .exhaustive();

    const handleSidebarItemClick = (tab: MapLayoutTabs) => {
        const sameTab = sidebarTab === tab;
        setSidebarTab(tab);
        onSidebarItemClick?.(tab);

        setOpen((prev) => {
            if (!prev) {
                onSidebarOpenChange?.(true, tab);
                return true;
            }
            if (!sameTab) {
                return prev;
            }

            onSidebarOpenChange?.(false, tab);
            return false;
        });
    };

    return (
        <MapDrawerLayout
            {...sizes}
            sidebar={
                <Stack
                    gap={1}
                    height={"100%"}
                    sx={{
                        padding: 3,
                        borderRight: 1,
                        borderColor: (theme) => theme.palette.stroke[100],
                    }}
                >
                    {pipe(
                        mapLayoutTabs,
                        A.map((tab) => ({
                            tab,
                            node: {
                                datasets,
                                shapeLayers,
                                boundaries,
                                maps,
                                configuration,
                            }[tab],
                        })),
                        A.filter(({ node }) => !!node),
                        A.map(({ tab }) => (
                            <NavigationRailIcon
                                key={tab}
                                square
                                focused={open && sidebarTab === tab}
                                onClick={() => handleSidebarItemClick(tab)}
                            >
                                {getTabIcon(tab)}
                            </NavigationRailIcon>
                        ))
                    )}
                </Stack>
            }
            tabBar={
                <Grid
                    container
                    justifyContent="space-around"
                    flexWrap="nowrap"
                    gap={1}
                    sx={{
                        position: "relative",
                        padding: 3,
                        borderTop: 1,
                        borderColor: (theme) => theme.palette.stroke[100],
                        // zIndex of drawer is reserved for the sidebar backdrop on mobile
                        zIndex: (theme) => theme.zIndex.drawer - 1,
                        backgroundColor: (theme) =>
                            theme.palette.background.container,
                    }}
                >
                    <NavigationRailIcon
                        square
                        focused={open && sidebarTab === "datasets"}
                        onClick={() => handleSidebarItemClick("datasets")}
                    >
                        <TableRowsOutline />
                    </NavigationRailIcon>
                    <NavigationRailIcon
                        square
                        focused={open && sidebarTab === "shapeLayers"}
                        onClick={() => handleSidebarItemClick("shapeLayers")}
                    >
                        <LayersOutline />
                    </NavigationRailIcon>
                    <NavigationRailIcon
                        square
                        focused={open && sidebarTab === "boundaries"}
                        onClick={() => handleSidebarItemClick("boundaries")}
                    >
                        <CropFreeOutline />
                    </NavigationRailIcon>
                    <NavigationRailIcon
                        square
                        focused={open && sidebarTab === "maps"}
                        onClick={() => handleSidebarItemClick("maps")}
                    >
                        <MapOutline />
                    </NavigationRailIcon>
                    <NavigationRailIcon
                        square
                        focused={open && sidebarTab === "configuration"}
                        onClick={() => handleSidebarItemClick("configuration")}
                    >
                        <TuneOutline />
                    </NavigationRailIcon>
                </Grid>
            }
            // ----------LEFT----------
            left={
                <FlexScrollAreaContainer sx={{ overflow: "hidden" }}>
                    <MapLayoutHeader
                        header={
                            sidebarTab === "datasets" && datasetsTabLabel
                                ? datasetsTabLabel
                                : startCase(sidebarTab)
                        }
                        onClick={() => {
                            setOpen(false);
                            onSidebarOpenChange?.(false, sidebarTab);
                        }}
                    />
                    <FlexScrollArea flexDirection="column">
                        {
                            {
                                datasets,
                                shapeLayers,
                                boundaries,
                                maps,
                                configuration,
                            }[sidebarTab]
                        }
                    </FlexScrollArea>
                </FlexScrollAreaContainer>
            }
            leftOpen={open}
            onLeftOpenChange={(open) =>
                setOpen((prev) => {
                    if (open !== prev) {
                        onSidebarOpenChange?.(open, sidebarTab);
                        return open;
                    }
                    return prev;
                })
            }
            // ----------BOTTOM----------
            bottom={({ hasTransitioned }) => (
                <Box width="100%" height="100%" sx={{ overflow: "hidden" }}>
                    {bottom?.({ hasTransitioned })}
                </Box>
            )}
            bottomOpen={bottomOpen}
            // ----------TOP----------
            top={
                <Box width="100%" height="100%" sx={{ overflow: "hidden" }}>
                    {top}
                </Box>
            }
            topOpen={topOpen}
        >
            {children}
        </MapDrawerLayout>
    );
};
